/**
 * Which permissions a page needs, in one place: the sidebar, the icon rail and the route guards all read this,
 * so a nav item is shown exactly when its route would render. "Any of" semantics, held anywhere (workspace-wide or
 * at any building/team scope) - pages then filter their content by scope, and the API stays the authority.
 * Paths without an entry are open to every member. Settings (`/settings/...`) gates each section itself
 * (features/settings/registry.ts); the old /admin and /workforce/settings URLs redirect there.
 */
import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import { DASHBOARD_PERMS, ON_SITE_PERMS, moduleForPath, pathAvailable, type MyPermissions } from '@tradeoutlet/shared';
import { useQuery } from '@tanstack/react-query';
import { usePermissions } from './permissions';
import { api } from './api';
import { useWorkspaceId } from './hooks';
import { qk } from './queryClient';
import { ModuleOff } from './modules';

const FLEET_ANY = ['fleet.view', 'fleet.journeys.plan', 'fleet.vehicles.manage', 'fleet.drivers.manage', 'fleet.reports.view'] as const;
export const ROUTE_ACCESS: Record<string, readonly string[]> = {
  '/assistant': ['assistant.use'],
  '/forms': ['forms.template.manage'],
  '/workforce/schedule': ['workforce.rota.view_drafts', 'workforce.rota.edit'],
  '/workforce/reports': ['workforce.reports.view', 'workforce.audit.view', 'sales.view', 'sales.bonus.approve', 'workforce.pay.view'],
  '/workforce/handovers': ['workforce.handover.view', 'workforce.handover.templates'],
  '/workforce/handover-templates': ['workforce.handover.templates'],
  '/workforce/skills': ['workforce.skills.manage'],
  '/fleet/my-runs': ['fleet.drive'],
  // t335u: the equipment register (report-issues people check and report; issue-settings or template managers run it).
  '/equipment': ['ops.incident.report', 'ops.incident.settings', 'ops.checklist.template.manage'],
  // t335u: visitors, on site now and the fire roll call (managers of the building's time, kiosks or issues).
  '/visitors': ON_SITE_PERMS,
  // t335u: the manager dashboard (any one of its tiles' permissions, at any building).
  '/dashboard': DASHBOARD_PERMS,
  // t554u: Store sales (the API also checks each building).
  '/sales': ['sales.view'],
  // t563u: the approvals list in the menu. The page itself is open to anyone a review stage names (no Guard on the route).
  '/policies/approvals': ['policies.approve', 'policies.manage'],
  // Settings > People & access > Users, also listed under People & shifts > People (same perms as its settings section).
  '/settings/people/users': ['admin.users.manage', 'admin.roles.manage', 'workforce.employees.edit', 'workforce.settings.manage', 'hr.view', 'hr.fields.manage', 'hr.documents.view', 'hr.documents.manage'],
  '/fleet': FLEET_ANY,
  // Fleet pages (were tabs on /fleet): same rules the tabs had.
  // t636u: the Fleet dashboard's tiles are fleet.view; its live map card also needs tracking (as /fleet/live).
  '/fleet/dashboard': ['fleet.view'],
  '/fleet/journeys': FLEET_ANY,
  '/fleet/calendar': FLEET_ANY,
  '/fleet/vehicles': FLEET_ANY,
  '/fleet/drivers': ['fleet.view'],
  '/fleet/hauliers': ['fleet.view'],
  '/fleet/reports': ['fleet.reports.view'],
  // t541u: vehicle locations during journeys.
  '/fleet/live': ['fleet.tracking.view', 'fleet.journeys.plan'],
};

type Mine = Pick<MyPermissions, 'workspace' | 'scoped' | 'modules_off'> | null | undefined;
export const holdsAnywhere = (mine: Mine, p: string) => Boolean(mine && (mine.workspace.includes(p) || mine.scoped.some(s => s.permissions.includes(p))));

/** Self-employed driver accounts hold only fleet.drive: they get the driver-only shell (the API enforces the same limit). */
export const isExternalDriver = (mine: Mine) => Boolean(mine && mine.workspace.length > 0 && mine.workspace.every(p => p === 'fleet.drive') && mine.scoped.every(s => s.permissions.every(p => p === 'fleet.drive')));

/** Longest matching prefix rule for a path (so /workforce/reports/hours uses /workforce/reports). */
export function ruleFor(path: string): readonly string[] | null {
  const hit = Object.keys(ROUTE_ACCESS).filter(k => path === k || path.startsWith(`${k}/`)).sort((a, b) => b.length - a.length)[0];
  return hit ? ROUTE_ACCESS[hit] : null;
}
/** t369u: false too when the page's module is switched off for the workspace (so it leaves every menu). */
export function canAccess(mine: Mine, path: string): boolean {
  if (!pathAvailable(mine?.modules_off, path)) return false;
  const rule = ruleFor(path);
  return !rule || rule.some(p => holdsAnywhere(mine, p));
}

/** Hook form; re-renders when the permission cache is invalidated (role/grant edits, window focus, 60s staleness). */
export function useNavAccess() {
  const q = usePermissions();
  // t563u: policy approvers are chosen per stage (people or positions), so anyone with a policy waiting for them can
  // open "Waiting for me", even without the approve permission. Same query as the nav badges, so no extra request.
  const ws = useWorkspaceId();
  const badges = useQuery({ queryKey: qk.navBadges(ws ?? ''), queryFn: () => api.navBadges(ws!), enabled: Boolean(ws), refetchInterval: 60_000, staleTime: 30_000 });
  const policyWaiting = (badges.data?.items['/policies/approvals'] ?? 0) > 0;
  const can = useCallback((path: string) => canAccess(q.data, path) || (policyWaiting && path === '/policies/approvals'), [q.data, policyWaiting]);
  return { ready: !q.isLoading && !badges.isLoading, can, off: q.data?.modules_off };
}

export function NoAccess() {
  return (
    <div className="flex h-full items-center justify-center p-8" data-testid="no-access">
      <div className="max-w-sm text-center">
        <ShieldOff className="mx-auto mb-3 h-10 w-10 text-slate-500" />
        <h1 className="text-lg font-semibold text-slate-900 dark:text-white">You don’t have access to this page</h1>
        <p className="mt-1 text-sm text-slate-500">Ask an admin or your manager if you think you should. Your role may also have changed recently.</p>
        <Link to="/" className="mt-4 inline-block rounded-md bg-brand-yellow px-3 py-1.5 text-sm font-medium text-on-accent-strong">Back to Home</Link>
      </div>
    </div>
  );
}

/** Route guard: renders the page only when canAccess(path) (or `also`, t554u); waits for permissions rather than flashing "no access". */
export function Guard({ path, also, children }: { path: string; also?: string; children: ReactNode }) {
  const a = useNavAccess();
  if (!a.ready) return null;
  return <>{a.can(path) || (also && a.can(also)) ? children : pathAvailable(a.off, path) ? <NoAccess /> : <ModuleOff module={moduleForPath(path)} />}</>;
}

/** Can plan fleet journeys somewhere: shows "Plan delivery" on tasks and messages. */
export function useCanPlanJourneys() { return holdsAnywhere(usePermissions().data, 'fleet.journeys.plan'); }
